import { fromEvent } from 'rxjs'
import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'
import { User } from '@/services/user'
import { useBrandCustomStore } from './brand-custom.service'
import { notifyPopupService, servicePermission, team, user } from '@/common/basis/root-injector'
import { PopupKey } from '@/views/dashboard/_notify-popup/notify-popup.service'
import { useRolePermStore } from '@/services/role-perm.service'
import { defaultErrorAction } from '@/common/error/error-code'
import { router } from '@/router'
import { getUrlParam } from '@/utils/url-params'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { UserInfo, VerifyResponseData } from '@/views/auth-mfa/service'
import { ENABLE_MFA } from '@/common/const/build-locale'

const isTestDev = (process.env.NODE_ENV === 'production' && process.env.STAGE === 'development') || process.env.NODE_ENV === 'development' || window.location.host.includes('aix-test-k8s.iweikan.cn') || window.location.host.includes('6666ppt.com')

const thatWindowUrl = `${isTestDev ? 'http:' : 'https:'}//${window.location.host}`
const proxyUrl = `${thatWindowUrl}/vip_login_proxy.html`

/**
 * 生成 16 位 16进制随机字符串
 */
function randomHexString () {
  // eslint-disable-next-line no-magic-numbers
  return `${Math.random().toString(16)}${Math.random().toString(16)}`.replace(/\./g, '').split('').sort(() => {
    // eslint-disable-next-line no-magic-numbers
    return Math.random() - 0.5
    // eslint-disable-next-line no-magic-numbers
  }).join('').substr(0, 16)
}

interface QQLoginData {
  access_token: string;
  redirect_url: string;
}

interface WechatLoginData {
  authorization_code: string;
}

export interface MobileLoginData {
  phone: string;
  code: string;
  type: string;
}

/**
 * 1 -- QQ登录
 * 2 -- 微信APP登录
 * 3 -- 微信h5登录
 * 4 -- 手机登录
 * 5 -- 快速登录
 * 6 -- 游客登录
 * 7 -- 微信小程序登录
 * 8 -- qq h5登录
 * 12 -- 华为云商店登录
 */
export enum LoginType {
  qq = 1,
  // eslint-disable-next-line no-magic-numbers
  wechat = 3,
  // eslint-disable-next-line no-magic-numbers
  mobile = 4,
  // eslint-disable-next-line no-magic-numbers
  qqH5 = 8,
  // eslint-disable-next-line no-magic-numbers
  hw = 10,
  /** 账号密码 */
  user = 11,

  /** 级联登录（前端自定义登录类型） */
  cascade = 101,
}

export enum VncPdType {
  /** 已设置 */
  alreadySet = 1,
  /** 未设置 */
  notSet = 2,
}

export interface HwJoint {
  code: string
  tenant: string
}

export interface UserLoginData {
  user_name: string;
  password: string;
   /** 团队id|企业id (日文版必须要传) */
  team_id?: string
}

export interface LoginData {
  login_type: LoginType;
  qq?: QQLoginData;
  wx?: WechatLoginData;
  mobile?: MobileLoginData;
  hw_joint?: HwJoint;
  user?: UserLoginData
}

export interface LoginUserInfo {
  avatar: string
  nick: string
  is_new_user: boolean
  register_time: string
  is_tourist: boolean
  mobile: string
  user_name: string
  is_exist_wx_union: boolean
  is_exist_qq_union: boolean
  is_account_user: boolean
  need_update_pwd: boolean
  is_first_login: boolean
  is_bind_mobile: boolean
  is_bind_wx: boolean
  /** 1-已设置 2-未设置 */
  vnc_password: number
  /** 级联登录时，父级团队地址(协议+ip+端口，如：http://10.12.36.128:8089) */
  parent_team_origin?: string
  /** 邮箱 */
  email?: string
}

export interface LoginResponse {
 /** 用户令牌 */
  token: string
  /** 用户唯一id */
  uid: string
  /** 是否需要 MFA 二次验证 */
  mfa_required: boolean
  /** MFA 二次验证临时 token */
  mfa_token: string
  /** 是否已绑定 MFA（mfa_required=true 时：true → /user/mfa/verify；false → /user/mfa/generate → /user/mfa/verify） */
  mfa_bound: boolean
  /** [go_type=UserInfo] */
  user_info: UserInfo
  payload_return?: any
}
@Injectable()
export class LoginService {
  private logging = false

  constructor (private http: Http, private user: User) {
  }

  getVerifyCode (mobile: string) {
    return this.http.post('/user/send_code', {
      mobile,
    })
  }

  login (data: LoginData): Promise<LoginResponse> {
    if (this.logging) return
    this.logging = true
    this.user.cleanUser()
    team.cleanTeam()
    useRolePermStore().clearCurrentRolePerms()
    servicePermission.clear()
    return this.http.post<LoginResponse>(this.getLoginUrl(data.login_type), data).then(response => {
      // SaaS 暂不支持 MFA，直接走正常登录
      if (!ENABLE_MFA) {
        this.finalizeLogin(response)
        return response
      }
      // MFA 需要二次验证：不 finalize，返回 mfa_token 给调用方
      if (response.mfa_required && response.mfa_token) {
        return response
      }
      // 正常登录（无需 MFA）
      this.finalizeLogin(response)
      return response
    }).finally(() => {
      setTimeout(() => this.logging = false, 1000)
    })
  }

  /** 完成登录后处理：写入用户信息、触发弹窗 */
  private finalizeLogin (response: LoginResponse | VerifyResponseData) {
    this.user.updateUserinfo({
      // eslint-disable-next-line no-magic-numbers
      ...(response.user_info || {} as any),
      token: response.token,
      uid: response.uid,
      parent_team_origin: getUrlParam('parent_team_origin') || ''
    })
    notifyPopupService.unReadyUserPopup()
    notifyPopupService.popupToReady(PopupKey.updatePassword)
    notifyPopupService.popupToReady(PopupKey.allServicesExpired)
  }

  getLoginUrl (type: LoginType) {
    if (type === LoginType.cascade) {
      return '/cascade/password_free_login'
    }
    return '/user/login'
  }

  async doLogin (data: LoginData): Promise<LoginResponse | void> {
    const response = await this.login(data)
    // SaaS 暂不支持 MFA，直接跳转
    if (!ENABLE_MFA) {
      return this.navigateAfterLogin()
    }
    // MFA 需要二次验证：返回 response 由调用方处理
    if (response?.mfa_required) {
      return response
    }
    return this.navigateAfterLogin()
  }

  /** 登录完成后的团队刷新与路由跳转 */
  private navigateAfterLogin () {
    return Promise.all([team.refreshTeamList(), team.getInviteTeamList()])
      .then(([teamResponse, inviteResponse]) => {
        if (teamResponse.list.length !== 1 || inviteResponse.list?.length > 0) {
          router.replace('/team')
        } else {
          return team.updateCurrentTeam(teamResponse.list[0]).then(() => {
            router.replace('/dashboard')
          })
        }
      }).catch(e => {
        if (e.responseBody?.ret === 4802010) {
          router.replace('/team')
        } else {
          defaultErrorAction(e)
        }
        throw new Error()
      })
  }

  /** MFA 验证通过后完成登录：写入用户信息 + 团队刷新 + 路由跳转 */
  finalizeMfaLogin (verifyResponse: VerifyResponseData): Promise<void> {
    this.finalizeLogin(verifyResponse)
    return this.navigateAfterLogin()
  }

  loginByWechat (): Promise<LoginResponse> {
    const config = {
      appId: 'wx346b463d236d169e',
      responseType: 'code',
      scope: 'snsapi_login'
    }
    const wechatProxyUrl = `${proxyUrl}?postmessage_domain=${thatWindowUrl}&type=wechat`
    const verifyCode = randomHexString()
    const redirectUri = encodeURIComponent(wechatProxyUrl)

    const authPageBaseUri = 'https://open.weixin.qq.com/connect/qrconnect'
    const authParams = `?appid=${config.appId}&redirect_uri=${redirectUri}&response_type=${config.responseType}&scope=${config.scope}&state=${verifyCode}#wechat_redirect`
    const url = authPageBaseUri + authParams

    return this.openWindow(url, verifyCode, 'wechat')
  }

  loginByQQ (): Promise<LoginResponse> {
    const config = {
      clientId: '101920689',
      responseType: 'code',
      scope: 'get_user_info',
      state: 'state'
    }

    const qqProxyUrl = `${proxyUrl}?postmessage_domain=${thatWindowUrl}&type=qq`

    const verifyCode = randomHexString()
    const redirectUri = encodeURIComponent(qqProxyUrl)

    const authPageBaseUri = 'https://graph.qq.com/oauth2.0/authorize'
    const authParams = `?client_id=${config.clientId}&response_type=${config.responseType}&redirect_uri=${redirectUri}&scope=${config.scope}&state=${verifyCode}`
    const url = authPageBaseUri + authParams

    return this.openWindow(url, verifyCode, 'qq', redirectUri)
  }

  private openWindow (url: string, verifyCode: string, type: 'qq' | 'wechat', redirectUri?: string): Promise<LoginResponse> {
    // eslint-disable-next-line no-magic-numbers
    const top = window.screen.height / 2 - 250
    // eslint-disable-next-line no-magic-numbers
    const left = window.screen.width / 2 - 450
    const { brandConfig } = useBrandCustomStore()

    window.open(url, brandConfig.client_name, `scrollbars=no, resizable=no, status=no, location=no, toolbar=no, menubar=no, width=900, height=500, top=${top}, left=${left}`)

    return new Promise(resolve => {
      const sub = fromEvent<MessageEvent>(window, 'message').subscribe(ev => {
        try {
          const response = JSON.parse(ev.data)
          if (response?.type !== 'login') {
            return
          }
          sub.unsubscribe()
          const search = response.data.search
          if (search?.state !== verifyCode || !search?.code) {
            return
          }
          switch (type) {
            case 'qq':
              resolve(this.login({
                login_type: LoginType.qqH5,
                qq: {
                  access_token: search.code,
                  redirect_url: redirectUri
                }
              }))
              break
            case 'wechat':
              resolve(this.login({
                login_type: LoginType.wechat,
                wx: {
                  authorization_code: search.code
                }
              }))
          }
        } catch (e) {
        }
      })
    })
  }
}

/**
 * 所有退出登录或返回登录页，都应该通过这个函数处理，以统一管理
 * @param {boolean} isClean - 是否清理用户和团队的本地数据
 */
export function toLoginPage (isClean?: boolean): Promise<any> {
  const { parent_team_origin } = user.getUserInfo() || {} as any // 先行缓存parent_team_origin，不然清除信息后就拿不到了
  if (isClean) {
    user.cleanUser()
    team.cleanTeam()
    useRolePermStore().clearCurrentRolePerms?.()
    servicePermission.clear()
  }

  if (parent_team_origin) {
    window.location.href = `${parent_team_origin}/dashboard/cascade-branch-overview?form=cascade_child_team`
    return Promise.resolve()
  }
  return router.push('/login')
}
